import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { usePermissions } from '../../hooks/usePermissions';

/**
 * ProtectedRoute component for role-based route access control
 * Redirects unauthorized users to /unauthorized page
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Child components to render if authorized
 * @param {string[]} props.allowedRoles - Array of roles allowed to access this route
 * @param {string} props.redirectTo - Optional custom redirect path (defaults to /unauthorized)
 */
const ProtectedRoute = ({
  children,
  allowedRoles = [],
  redirectTo = '/unauthorized',
}) => {
  const { userRole, hasAnyRole } = usePermissions();
  const navigate = useNavigate();
  const location = useLocation();

  // Normalize roles to lowercase for comparison
  const normalizedAllowedRoles = allowedRoles.map((role) => role.toLowerCase());

  // Check if user has permission (user is always authenticated inside <SignedIn>)
  const hasPermission = hasAnyRole(normalizedAllowedRoles);

  useEffect(() => {
    // If user doesn't have permission, redirect to unauthorized
    if (!hasPermission) {
      console.warn(
        `[ProtectedRoute] Access denied: User with role "${userRole}" attempted to access "${location.pathname}"`
      );
      navigate(redirectTo, {
        state: { from: location.pathname },
        replace: true,
      });
    }
  }, [hasPermission, userRole, navigate, redirectTo, location]);

  // If user has permission, render children
  if (hasPermission) {
    return <>{children}</>;
  }

  // While redirecting, show nothing (prevents flash of unauthorized content)
  return null;
};

export default ProtectedRoute;
